class CartManager {
  constructor() {
    this.selectors = {
      activeSlide: '.carousel__slide.swiper-slide-active',
      visiblePicture: '.carousel__picture[style*="display: block"]',
      addButton: '.product-list__add-button',
      cartDrawer: 'cart-drawer',
      drawerInner: '.drawer__inner',
      cartCount: '#cart-icon-bubble .cart-text-desktop',
      productCard: '.carousel__product-card'
    };
    
    this.cache = {
      variants: new Map(),
      properties: new Map()
    };
    
    this.isProcessing = false;
    this.bindEvents();
  }

  bindEvents() {
    const addButton = document.querySelector(this.selectors.addButton);
    if (addButton) {
      addButton.addEventListener('click', () => this.addAllProductsToCart());
    }
  }

  async getProductProperties(productUrl) {
    if (this.cache.properties.has(productUrl)) {
      return this.cache.properties.get(productUrl);
    }

    try {
      const url = new URL(productUrl, window.location.origin);
      const properties = {};

      if (url.pathname.includes('/products/naroznik')) {
        const ioParams = url.searchParams.get('io')?.split(',');
        if (ioParams?.length >= 3) {
          properties.Base = ioParams[0];
          properties.Armrest = ioParams[1];
          properties.Configuration = ioParams[2];
        }
      }

      this.cache.properties.set(productUrl, properties);
      return properties;
    } catch (error) {
      console.error('Error getting product properties:', error);
      return {};
    }
  }

  async getVariantId(href) {
    if (!href) return null;
    
    if (this.cache.variants.has(href)) {
      return this.cache.variants.get(href);
    }

    try {
      const fullUrl = href.startsWith('http') ? href : `https://${window.location.host}${href}`;
      const urlObj = new URL(fullUrl);
      
      const variantId = urlObj.searchParams.get('variant');
      if (variantId) {
        this.cache.variants.set(href, variantId);
        return variantId;
      }

      const productHandle = urlObj.pathname.split('/products/')[1]?.split('?')[0];
      if (!productHandle) return null;

      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) return null;

      const productData = await response.json();
      const defaultVariantId = productData.variants?.[0]?.id;
      
      if (defaultVariantId) {
        this.cache.variants.set(href, defaultVariantId);
      }
      return defaultVariantId;
    } catch (error) {
      console.error('Error in getVariantId:', error);
      return null;
    }
  }

  async addAllProductsToCart() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    const addButton = document.querySelector(this.selectors.addButton);
    if (addButton) addButton.disabled = true;

    try {
      const products = await this.getActiveProducts();
      if (!products.length) return;

      const items = await this.processProducts(products);
      if (!items.length) return;

      await this.submitCartAdd(items);
      await this.updateCartDrawer();
    } catch (error) {
      console.error('Error in addAllProductsToCart:', error);
      this.showError('Failed to add items to cart. Please try again.');
    } finally {
      if (addButton) addButton.disabled = false;
      this.isProcessing = false;
    }
  }

  async getActiveProducts() {
    const activeSlide = document.querySelector(this.selectors.activeSlide);
    if (!activeSlide) return [];

    const visiblePicture = activeSlide.querySelector(this.selectors.visiblePicture);
    if (!visiblePicture) return [];

    return Array.from(visiblePicture.querySelectorAll(this.selectors.productCard));
  }

  async processProducts(productCards) {
    const processPromises = productCards.map(async (card) => {
      const href = card.getAttribute('href');
      const productId = card.closest('.carousel__hotspot')?.getAttribute('data-product-id');
      if (!href || !productId) return null;

      const [variantId, properties] = await Promise.all([
        this.getVariantId(href),
        this.getProductProperties(href)
      ]);

      if (!variantId) return null;

      return {
        id: variantId,
        quantity: 1,
        properties
      };
    });

    const items = await Promise.all(processPromises);
    return items.filter(Boolean);
  }

  async submitCartAdd(items) {
    const response = await fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items })
    });

    const cartData = await response.json();
    if (cartData.status) {
      throw new Error(cartData.description);
    }
    return cartData;
  }

  async updateCartDrawer() {
    const cartDrawer = document.querySelector(this.selectors.cartDrawer);
    if (!cartDrawer) return;

    try {
      const [drawerContent, cartCount] = await Promise.all([
        this.fetchDrawerContent(),
        this.fetchCartCount()
      ]);

      this.updateDrawerHTML(cartDrawer, drawerContent);
      this.updateCartCounter(cartCount);
      
      cartDrawer.classList.remove('is-empty');
      cartDrawer.open();
    } catch (error) {
      console.error('Error updating cart drawer:', error);
      throw error;
    }
  }

  async fetchDrawerContent() {
    const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
    if (!response.ok) throw new Error('Failed to fetch cart content');
    return response.text();
  }

  async fetchCartCount() {
    const response = await fetch('/cart.js');
    if (!response.ok) throw new Error('Failed to fetch cart count');
    const data = await response.json();
    return data.item_count;
  }

  updateDrawerHTML(cartDrawer, html) {
    const parser = new DOMParser();
    const cartDoc = parser.parseFromString(html, 'text/html');

    const drawerContent = cartDrawer.querySelector(this.selectors.drawerInner);
    const newContent = cartDoc.querySelector(this.selectors.drawerInner);

    if (drawerContent && newContent) {
      drawerContent.innerHTML = newContent.innerHTML;
    }
  }

  updateCartCounter(count) {
    const counter = document.querySelector(this.selectors.cartCount);
    if (counter) {
      counter.innerHTML = `Cart (${count})`;
    }
  }

  showError(message) {
    // Implement your error display logic here
    console.error(message);
  }
}

// Initialize cart manager
const cartManager = new CartManager();
window.addAllProductsToCart = () => cartManager.addAllProductsToCart();